import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Package, Users, TrendingUp, Clock, CheckCircle, AlertCircle, Star, DollarSign, CalendarCheck, XCircle, RotateCcw, Award, BarChart3 } from 'lucide-react';
import { formatPrice, isAdminRole, isSuperAdminRole } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';
import { obtenerProductos } from '../api/productos';
import { obtenerReservas } from '../api/reservas';
import { getHorarioSemanal } from '../api/agenda';
import { useNotify } from '../components/NotificationProvider';
import MonthlyRevenueChart from '../components/MonthlyRevenueChart';
import SimplePieChart from '../components/SimplePieChart';
import SimpleHorizontalBarChart from '../components/SimpleHorizontalBarChart';
import SimpleBarChart from '../components/SimpleBarChart';

const AdminDashboard = ({ user, onLoginClick }) => {
  const navigate = useNavigate();
  const { error: notifyError } = useNotify();
  const [products, setProducts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [weekly, setWeekly] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [prods, resv, semanal] = await Promise.all([
          obtenerProductos(user),
          obtenerReservas(user),
          getHorarioSemanal(),
        ]);
        setProducts(Array.isArray(prods) ? prods : []);
        setBookings(Array.isArray(resv) ? resv : []);
        setWeekly(Array.isArray(semanal) ? semanal : []);
      } catch (e) {
        notifyError('No se pudo cargar el dashboard: ' + (e?.message || 'Error'));
      }
    };
    load();
  }, [user?.id, user?.role, user?.email, user?.telefono]);

  if (!user || !isAdminRole(user.role)) {
    return (
      <motion.div 
        className="text-center py-20 bg-white/90 backdrop-blur-xl rounded-3xl shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <img src="/images/001.gif"  alt="Mora.Artt Logo" className="w-32 h-32 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Acceso Restringido</h2>
        <p className="text-gray-600 mb-6">
          Solo los administradores tienen acceso a esta sección. Por favor, inicia sesión con una cuenta de administrador.
        </p>
        <motion.button
          onClick={onLoginClick}
          className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Iniciar Sesión
        </motion.button>
      </motion.div>
    );
  }

  const isRevenueStatus = (estado) => {
    const st = String(estado || '').toLowerCase();
    return st === 'confirmada' || st === 'confirmado' || st === 'aceptada' || st === 'pagada' || st === 'aprobada';
  };

  // Normaliza strings de precio como "$15.000" o "25,000.50" a número
  const parsePrice = (v) => {
    if (v == null) return 0;
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    const s = String(v).trim();
    if (!s) return 0;
    // Remover símbolo de moneda y espacios
    let clean = s.replace(/[$CLP\s]/gi, '');
    // Si hay miles con puntos y sin coma decimal (formato CL), quita los puntos
    // Si hay coma decimal, reemplaza por punto
    // Ej: 15.000 -> 15000, 25.000,50 -> 25000.50
    clean = clean.replace(/\./g, '').replace(/,/g, '.');
    const n = parseFloat(clean);
    return isNaN(n) ? 0 : n;
  };

  const getPriceForReserva = (r) => {
    const direct = parsePrice(r.Precio);
    if (!isNaN(direct) && direct > 0) return direct;
    const pid = r.ProductoId || r.ProductoID || r.IdProducto || r.idProducto;
    let prod = null;
    if (pid != null) {
      prod = products.find(p => p.Id === pid || p.ID === pid || p.id === pid);
    }
    if (!prod && r.NombreProducto) {
      const nombre = String(r.NombreProducto).toLowerCase();
      prod = products.find(p => String(p.Nombre).toLowerCase() === nombre);
    }
    return parsePrice(prod?.Precio);
  };

  const stats = useMemo(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const isThisMonth = (fechaStr) => {
      if (!fechaStr) return false;
      const d = new Date(fechaStr);
      return !isNaN(d) && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    };

    const isThisWeek = (fechaStr) => {
      if (!fechaStr) return false;
      const d = new Date(fechaStr);
      return !isNaN(d) && d >= startOfWeek && d <= endOfWeek;
    };

    const isFuture = (fechaStr, horaStr) => {
      if (!fechaStr) return false;
      const d = new Date(`${fechaStr}T${horaStr || '00:00'}`);
      return !isNaN(d) && d > now;
    };

    const activeProducts = products.filter(p => p.Disponible === true || p.Disponible === 1 || String(p.Disponible).toLowerCase() === 'true').length;
    const pending = bookings.filter(b => (b.Estado || '').toLowerCase() === 'pendiente').length;
    const confirmed = bookings.filter(b => isRevenueStatus(b.Estado)).length;
    const cancelled = bookings.filter(b => (b.Estado || '').toLowerCase() === 'cancelada').length;
    const rescheduled = bookings.filter(b => (b.Estado || '').toLowerCase() === 'reagendada' || (b.Estado || '').toLowerCase() === 'pospuesta').length;

    const revenueMonth = bookings
      .filter(b => isRevenueStatus(b.Estado) && isThisMonth(b.CreadoEn || b.Fecha))
      .reduce((acc, r) => acc + getPriceForReserva(r), 0);

    const revenueWeek = bookings
      .filter(b => isRevenueStatus(b.Estado) && isThisWeek(b.CreadoEn || b.Fecha))
      .reduce((acc, r) => acc + getPriceForReserva(r), 0);

    const uniqueClients = new Set(bookings.map(b => b.UsuarioId || b.Email || b.NombreCliente)).size;

    const ratings = bookings
      .map(b => Number(b.Calificacion))
      .filter(n => Number.isFinite(n) && n > 0);
    const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length) : 0;

    const upcoming = bookings.filter(b => isFuture(b.Fecha, b.Hora)).length;

    const serviceCounts = {};
    bookings.forEach(b => {
      const name = b.NombreProducto || 'Sin servicio';
      serviceCounts[name] = (serviceCounts[name] || 0) + 1;
    });
    const topService = Object.entries(serviceCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

    const confirmationRate = bookings.length ? Math.round((confirmed / bookings.length) * 100) : 0;

    return {
      totalProducts: products.length,
      activeProducts,
      totalBookings: bookings.length,
      pendingBookings: pending,
      confirmedBookings: confirmed,
      cancelledBookings: cancelled,
      rescheduledBookings: rescheduled,
      totalRevenue: revenueMonth,
      weekRevenue: revenueWeek,
      uniqueClients,
      avgRating,
      upcoming,
      topService,
      confirmationRate,
    };
  }, [products, bookings]);

  // Serie de ingresos últimos 12 meses (incluye confirmadas/aceptadas)
  const revenueSeries = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth();
      const value = bookings
        .filter(b => {
          const bd = new Date(b.CreadoEn || b.Fecha);
          return !isNaN(bd) && bd.getFullYear() === year && bd.getMonth() === month && isRevenueStatus(b.Estado);
        })
        .reduce((acc, r) => acc + getPriceForReserva(r), 0);
      const label = d.toLocaleString('es-CL', { month: 'short' });
      arr.push({ label, value });
    }
    return arr;
  }, [bookings, products]);

  const recentBookings = useMemo(() => {
    return [...bookings]
      .sort((a, b) => new Date(b.CreadoEn || b.Fecha) - new Date(a.CreadoEn || a.Fecha))
      .slice(0, 5);
  }, [bookings]);

  // Gráfico circular: reservas por estado
  const bookingStatusData = useMemo(() => {
    const counts = {
      pendiente: 0,
      confirmada: 0,
      cancelada: 0,
      reagendada: 0,
      otra: 0
    };
    bookings.forEach(b => {
      const st = (b.Estado || '').toLowerCase();
      if (st in counts) counts[st]++;
      else counts.otra++;
    });
    return [
      { label: 'Pendientes', value: counts.pendiente, color: '#f59e0b' },
      { label: 'Confirmadas', value: counts.confirmada, color: '#10b981' },
      { label: 'Canceladas', value: counts.cancelada, color: '#ef4444' },
      { label: 'Reagendadas', value: counts.reagendada, color: '#6366f1' },
      { label: 'Otras', value: counts.otra, color: '#9ca3af' },
    ];
  }, [bookings]);

  // Gráfico horizontal: servicios más reservados
  const topServicesData = useMemo(() => {
    const counts = {};
    bookings.forEach(b => {
      const name = b.NombreProducto || 'Sin servicio';
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, value], i) => ({
        label,
        value,
        color: ['#ec4899', '#a855f7', '#6366f1', '#3b82f6', '#14b8a6'][i % 5]
      }));
  }, [bookings]);

  // Reservas por día de la semana (lunes = 1)
  const weekDayData = useMemo(() => {
    const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    const counts = [0, 0, 0, 0, 0, 0, 0];
    bookings.forEach(b => {
      if (!b.Fecha) return;
      const d = new Date(b.Fecha);
      if (isNaN(d)) return;
      let idx = d.getDay() - 1;
      if (idx < 0) idx = 6;
      counts[idx]++;
    });
    return counts.map((value, i) => ({ label: dayNames[i], value }));
  }, [bookings]);

  // Próximas reservas (próximas 5)
  const upcomingBookings = useMemo(() => {
    const now = new Date();
    return [...bookings]
      .filter(b => {
        if (!b.Fecha) return false;
        const d = new Date(`${b.Fecha}T${b.Hora || '00:00'}`);
        return !isNaN(d) && d > now;
      })
      .sort((a, b) => new Date(`${a.Fecha}T${a.Hora || '00:00'}`) - new Date(`${b.Fecha}T${b.Hora || '00:00'}`))
      .slice(0, 5);
  }, [bookings]);

  // Días activos/inactivos de la agenda
  const daysInfo = useMemo(() => {
    const toBool = (v) => v === true || v === 1 || String(v).toLowerCase() === 'true';
    const dayNames = {1:'Lun',2:'Mar',3:'Mié',4:'Jue',5:'Vie',6:'Sáb',7:'Dom'};
    const present = new Set((weekly || []).map(d => Number(d.DiaSemana)));
    const active = (weekly || []).filter(d => toBool(d.Activo)).map(d => Number(d.DiaSemana));
    const inactiveExplicit = (weekly || []).filter(d => !toBool(d.Activo)).map(d => Number(d.DiaSemana));
    const allDays = [1,2,3,4,5,6,7];
    const missingAsInactive = allDays.filter(d => !present.has(d));
    const inactiveAll = Array.from(new Set([...inactiveExplicit, ...missingAsInactive])).sort((a,b)=>a-b);
    return {
      activeCount: active.length,
      inactiveCount: inactiveAll.length,
      inactiveLabels: inactiveAll.map(d => dayNames[d] || d).join(', ')
    };
  }, [weekly]);

  const statCards = [
    {
      title: 'Productos Activos',
      value: `${stats.activeProducts}/${stats.totalProducts}`,
      icon: Package,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    {
      title: 'Reservas Pendientes',
      value: stats.pendingBookings,
      icon: Clock,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'from-amber-50 to-orange-50'
    },
    {
      title: 'Reservas Confirmadas',
      value: stats.confirmedBookings,
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50'
    },
    {
      title: 'Reservas Canceladas',
      value: stats.cancelledBookings,
      icon: XCircle,
      color: 'from-red-500 to-rose-500',
      bgColor: 'from-red-50 to-rose-50'
    },
    {
      title: 'Ingresos del Mes',
      value: formatPrice(stats.totalRevenue),
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50'
    },
    {
      title: 'Ingresos Semana',
      value: formatPrice(stats.weekRevenue),
      icon: DollarSign,
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'from-emerald-50 to-teal-50'
    },
    {
      title: 'Tasa de Confirmación',
      value: `${stats.confirmationRate}%`,
      icon: CalendarCheck,
      color: 'from-lime-500 to-green-500',
      bgColor: 'from-lime-50 to-green-50'
    },
    {
      title: 'Próximas Reservas',
      value: stats.upcoming,
      icon: Calendar,
      color: 'from-indigo-500 to-violet-500',
      bgColor: 'from-indigo-50 to-violet-50'
    },
    {
      title: 'Clientes Atendidos',
      value: stats.uniqueClients,
      icon: Users,
      color: 'from-sky-500 to-blue-500',
      bgColor: 'from-sky-50 to-blue-50'
    },
    {
      title: 'Rating Promedio',
      value: stats.avgRating ? stats.avgRating.toFixed(1) : '—',
      icon: Star,
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'from-yellow-50 to-amber-50'
    },
    {
      title: 'Servicio Top',
      value: stats.topService,
      icon: Award,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'from-pink-50 to-rose-50'
    },
    {
      title: 'Días de Agenda',
      value: `${daysInfo.activeCount}/7 activos`,
      icon: Calendar,
      color: 'from-fuchsia-500 to-purple-500',
      bgColor: 'from-fuchsia-50 to-purple-50',
      note: daysInfo.inactiveCount > 0 ? `Inactivos: ${daysInfo.inactiveLabels}` : 'Todos activos'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isSuperAdminRole(user?.role)
            ? 'Panel de Administración'
            : `Panel de ${user?.nombreCompleto || user?.username || 'Manicurista'}`}
        </h1>
        <p className="text-gray-600">
          {isSuperAdminRole(user?.role)
            ? 'Vista general de todo el negocio'
            : 'Aquí ves solo tus productos, reservas e indicadores'}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} rounded-2xl p-5 border border-white/50`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ scale: 1.02, y: -2 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-r ${stat.color}`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-gray-600 font-medium text-sm leading-tight">{stat.title}</p>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{stat.value}</h3>
              {stat.note && (
                <p className="text-xs text-gray-500 mt-2">{stat.note}</p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg lg:col-span-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-pink-500" />
            <h2 className="text-xl font-bold text-gray-900">Ingresos de los últimos 12 meses</h2>
          </div>
          <MonthlyRevenueChart data={revenueSeries} height={220} />
        </motion.div>

        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-bold text-gray-900">Reservas por Estado</h2>
          </div>
          <SimplePieChart data={bookingStatusData} height={220} />
        </motion.div>
      </div>

      {/* Segunda fila de gráficos y listas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-6 h-6 text-indigo-500" />
            <h2 className="text-xl font-bold text-gray-900">Servicios más Reservados</h2>
          </div>
          {topServicesData.length > 0 ? (
            <SimpleHorizontalBarChart data={topServicesData} height={220} />
          ) : (
            <p className="text-gray-500 text-center py-10">Aún no hay reservas</p>
          )}
        </motion.div>

        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-900">Reservas por Día</h2>
          </div>
          <SimpleBarChart data={weekDayData} height={220} barColor="#6366f1" />
        </motion.div>

        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <CalendarCheck className="w-6 h-6 text-emerald-500" />
            <h2 className="text-xl font-bold text-gray-900">Próximas Reservas</h2>
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {upcomingBookings.length > 0 ? (
              upcomingBookings.map((booking, index) => (
                <motion.div
                  key={booking.Id || `${booking.ProductoId}-${booking.Fecha}-${booking.Hora}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{booking.NombreCliente || 'Cliente'}</h4>
                    <p className="text-xs text-gray-600">{booking.NombreProducto}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-900">{booking.Fecha}</p>
                    <p className="text-xs text-gray-500">{String(booking.Hora).slice(0, 5)}</p>
                  </div>
                </motion.div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No hay próximas reservas</p>
            )}
          </div>
        </motion.div>
      </div>

      {/* Reservas recientes y productos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <RotateCcw className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-bold text-gray-900">Reservas Recientes</h2>
          </div>

          <div className="space-y-4">
            {recentBookings.length > 0 ? (
              recentBookings.map((booking, index) => {
                const product = products.find(p => p.Id === booking.ProductoId);
                return (
                  <motion.div
                    key={booking.Id || `${booking.ProductoId}-${booking.Fecha}-${booking.Hora}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{booking.NombreCliente || 'Cliente'}</h4>
                      <p className="text-sm text-gray-600">{booking.NombreProducto || product?.Nombre}</p>
                      <p className="text-xs text-gray-500">{booking.Fecha} - {String(booking.Hora).slice(0,5)}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        (booking.Estado || '').toLowerCase() === 'confirmada'
                          ? 'bg-green-100 text-green-800'
                          : (booking.Estado || '').toLowerCase() === 'pendiente'
                            ? 'bg-yellow-100 text-yellow-800'
                            : (booking.Estado || '').toLowerCase() === 'cancelada'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.Estado || '—'}
                      </span>
                      <p className="text-sm font-bold text-gray-900 mt-1">
                        {formatPrice(Number(booking.Precio) || Number(product?.Precio) || 0)}
                      </p>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-10">No hay reservas recientes</p>
            )}
          </div>
        </motion.div>

        <motion.div
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-6 h-6 text-pink-500" />
            <h2 className="text-xl font-bold text-gray-900">Mis Productos</h2>
          </div>

          <div className="space-y-4">
            {products.length > 0 ? (
              products
                .slice()
                .sort((a, b) => Number(b.Disponible) - Number(a.Disponible))
                .slice(0, 5)
                .map((product, index) => (
                  <motion.div
                    key={product.Id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={product.UrlImagen || '/images/001.gif'}
                        alt={product.Nombre}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-gray-900">{product.Nombre}</h4>
                        <p className="text-sm text-gray-600">{formatPrice(Number(product.Precio) || 0)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(product.Disponible === true || product.Disponible === 1 || String(product.Disponible).toLowerCase() === 'true') ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="text-sm font-medium text-gray-600">
                        {(product.Disponible === true || product.Disponible === 1 || String(product.Disponible).toLowerCase() === 'true') ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </motion.div>
                ))
            ) : (
              <p className="text-gray-500 text-center py-10">No tienes productos registrados</p>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;
